import { createFileRoute } from "@tanstack/react-router";
import { useOrganizationManagementGetPlatformStatistics } from "@/orval/organization-management";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Building, Users, GraduationCap, TrendingUp } from "lucide-react";
import { parseISO } from "date-fns";
import { ensureRouteQueryData } from "@/lib/route-loader";
import { getOrganizationManagementGetPlatformStatisticsQueryOptions } from "@/orval/organization-management";
import { PortalPageHeader } from "@/components/portal-page-header";

function SiteAdminDashboard() {
  const { data: stats, isLoading } = useOrganizationManagementGetPlatformStatistics();

  if (isLoading) {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-y-4 p-4 md:p-6">
        <PortalPageHeader className="mb-4" title="Dashboard" subtitle="Platform overview and statistics" />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
                <Skeleton className="h-4 w-[120px]" />
                <Skeleton className="size-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-7 w-[60px] mb-2" />
                <Skeleton className="h-3 w-[100px]" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
          <Card className="col-span-4">
            <CardHeader>
              <Skeleton className="h-6 w-[140px] mb-2" />
              <Skeleton className="h-4 w-[200px]" />
            </CardHeader>
            <CardContent className="pl-2">
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>
          <Card className="col-span-3">
            <CardHeader>
              <Skeleton className="h-6 w-[120px] mb-2" />
              <Skeleton className="h-4 w-[160px]" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-y-4 p-4 md:p-6">
      <PortalPageHeader className="mb-4" title="Dashboard" subtitle="Platform overview and statistics" />

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Organizations</CardTitle>
            <Building className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalOrganizations || 0}</div>
            <p className="text-xs text-muted-foreground">+{stats?.organizationGrowthLast30Days || 0} from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground">+{stats?.userGrowthLast30Days || 0} from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Classes</CardTitle>
            <GraduationCap className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalClasses || 0}</div>
            <p className="text-xs text-muted-foreground">Across all organizations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Growth Rate</CardTitle>
            <TrendingUp className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.totalOrganizations && stats?.organizationGrowthLast30Days
                ? Math.round((stats.organizationGrowthLast30Days / stats.totalOrganizations) * 100)
                : 0}
              %
            </div>
            <p className="text-xs text-muted-foreground">Organizations this month</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Organizations and Recent Activity */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Top Organizations</CardTitle>
            <CardDescription>Organizations with the most users</CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <div className="flex flex-col gap-y-4">
              {stats?.topOrganizations?.map((org, index) => (
                <div key={org.id} className="flex items-center">
                  <div className="flex flex-col ml-4 gap-y-1">
                    <p className="text-sm font-medium leading-none">{org.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {org.userCount} users • Created {parseISO(org.createdAt + "Z").toLocaleDateString()}
                    </p>
                  </div>
                  <div className="ml-auto font-medium">#{index + 1}</div>
                </div>
              ))}
              {(!stats?.topOrganizations || stats.topOrganizations.length === 0) && (
                <p className="text-sm text-muted-foreground">No organizations found</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest platform activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-y-4">
              {stats?.recentActivity?.map((activity) => (
                <div key={activity.id} className="flex items-center">
                  <div className="flex flex-col ml-4 gap-y-1">
                    <p className="text-sm font-medium leading-none">{activity.type}</p>
                    <p className="text-sm text-muted-foreground">
                      {activity.tableName} • {parseISO(activity.dateTime + "Z").toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {(!stats?.recentActivity || stats.recentActivity.length === 0) && (
                <p className="text-sm text-muted-foreground">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_app/site-admin/dashboard")({
  loader: (loaderContext) =>
    ensureRouteQueryData(loaderContext, getOrganizationManagementGetPlatformStatisticsQueryOptions()),
  component: SiteAdminDashboard,
});
